// Local persistence in IndexedDB: one record for the app state, one store for the attempt log.
// Nothing leaves the browser. If IndexedDB is unavailable (some private modes), the app keeps
// working in memory and says that progress won't be kept.

import type { Attempt, Persisted } from './model';

const DB_NAME = 'learning-greek';
const DB_VERSION = 1;
const KV = 'kv';
const ATTEMPTS = 'attempts';
const STATE_KEY = 'state';

let dbPromise: Promise<IDBDatabase | null> | null = null;
const memory: { state: Persisted | null; attempts: Attempt[] } = { state: null, attempts: [] };

function open(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve) => {
    try {
      if (typeof indexedDB === 'undefined') return resolve(null);
      const req = indexedDB.open(DB_NAME, DB_VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV);
        if (!db.objectStoreNames.contains(ATTEMPTS)) db.createObjectStore(ATTEMPTS, { autoIncrement: true });
      };
      req.onsuccess = () => {
        const db = req.result;
        // iOS can drop the connection after a long time in the background: reopen next time.
        db.onclose = () => {
          dbPromise = null;
        };
        db.onversionchange = () => {
          db.close();
          dbPromise = null;
        };
        resolve(db);
      };
      req.onerror = () => resolve(null);
      req.onblocked = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
  return dbPromise;
}

function once<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, body: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    let tx: IDBTransaction;
    try {
      tx = db.transaction(store, mode);
    } catch (err) {
      reject(err);
      return;
    }
    const req = body(tx.objectStore(store));
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
    tx.onabort = () => reject(tx.error);
  });
}

/** Runs a transaction; if the connection was lost, reconnects and tries once more. */
async function run<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, body: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  try {
    return await once(db, store, mode, body);
  } catch (err) {
    dbPromise = null;
    const fresh = await open();
    if (!fresh) throw err;
    return once(fresh, store, mode, body);
  }
}

export async function isPersistent(): Promise<boolean> {
  return (await open()) !== null;
}

export async function loadState(): Promise<Persisted | null> {
  const db = await open();
  if (!db) return memory.state;
  const value = await run<Persisted | undefined>(db, KV, 'readonly', (s) => s.get(STATE_KEY));
  return value ?? null;
}

export async function saveState(state: Persisted): Promise<void> {
  const db = await open();
  if (!db) {
    memory.state = structuredClone(state);
    return;
  }
  await run(db, KV, 'readwrite', (s) => s.put(state, STATE_KEY));
}

export async function addAttempt(attempt: Attempt): Promise<void> {
  const db = await open();
  if (!db) {
    memory.attempts.push(attempt);
    return;
  }
  await run(db, ATTEMPTS, 'readwrite', (s) => s.add(attempt));
}

export async function allAttempts(): Promise<Attempt[]> {
  const db = await open();
  if (!db) return [...memory.attempts];
  return run<Attempt[]>(db, ATTEMPTS, 'readonly', (s) => s.getAll() as IDBRequest<Attempt[]>);
}

export async function clearAll(): Promise<void> {
  const db = await open();
  memory.state = null;
  memory.attempts = [];
  if (!db) return;
  await run(db, KV, 'readwrite', (s) => s.clear());
  await run(db, ATTEMPTS, 'readwrite', (s) => s.clear());
}

/** Asks the browser to keep this site's storage under pressure. Best effort. */
export async function requestPersistence(): Promise<void> {
  try {
    await navigator.storage?.persist?.();
  } catch {
    // Not supported: nothing to do.
  }
}
